import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { Immutable, State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { getElementFieldValue, makeSetHandler, truncateWithEllipsis } from "../../globalStates/utils"
import Section from "../../components/Section"
import TextAreaField from "../../components/Field/TextArea"
import SelectField from "../../components/Field/Select"
import PageContents from "../../components/PageContents"
import { NotificationTemplate, NotificationTemplateInterest } from "../../globalStates/ExhibitorDataTypes"
import moment from "moment"
import "moment/locale/de"
import { convertHoursMinutesToDate, getHourFormat } from "../../utils/Dates"
import { NotificationTemplateDate, NotificationTemplateTime } from "../../branding/branding"
import { ReferenceDataType, withReferenceDataState } from "../../globalStates/ReferenceDataHOC"
import NotificationTemplateInterests from "./NotificationTemplatesInterests"
import BSForm from "react-bootstrap/Form"
import { RefHall } from "../../globalStates/ReferenceDataState"

function buildHallOptions(refHalls: Immutable<Array<RefHall>>) {
    return refHalls.map((refHall) => ({ id: refHall.value, label: refHall.name, value: refHall.value }))
}

interface NotificationTemplatesSectionProps {
    notificationTemplateState: State<NotificationTemplate, Validation>
    index: number
    onClickRemove: (productId: string) => void
}

const NotificationTemplatesSection: React.FC<NotificationTemplatesSectionProps & ReferenceDataType> = ({
    notificationTemplateState,
    refDataState,
    index,
    onClickRemove
}) => {
    moment.locale(useLanguageState().getLanguage())

    const branding = useLanguageState().getStrings()
    const notificationTemplate = useHookstate(notificationTemplateState)
    const sectionRef = useRef<HTMLDivElement>(null)

    const [date, setDate] = useState<string | null>(getInitialDate() || null)
    const [time, setTime] = useState<string | null>(getInitialTime() || null)
    const [standSpace, setStandSpace] = useState<string | null>(null)

    const refCategories = useMemo(() => refDataState.getCategories("INTEREST", []), [refDataState])
    const halls = useMemo(() => buildHallOptions(refDataState.getHalls()), [refDataState])

    const dates: { id: string; value: string; label: string }[] =
        branding.configuration.notificationTemplatesPage.sctNotificationTemplates.dates.map((item: NotificationTemplateDate) => ({
            id: item.id,
            value: item.date,
            label: `${moment(new Date(item.date)).format("dddd, DD.MM.yyyy")}`
        }))

    const times: { id: string; value: string; label: string }[] =
        branding.configuration.notificationTemplatesPage.sctNotificationTemplates.times.map((item: NotificationTemplateTime) => ({
            id: item.id,
            value: item.id,
            label:
                item.start === "00:00" && item.end === "23:59"
                    ? branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblEntireDay
                    : `${moment(convertHoursMinutesToDate(item.start)).format(getHourFormat())} - ${moment(
                          convertHoursMinutesToDate(item.end)
                      ).format(getHourFormat())}`
        }))

    function getInitialDate(): string | undefined {
        return branding.configuration.notificationTemplatesPage.sctNotificationTemplates.dates.find(
            (item: NotificationTemplateDate) => item.date === notificationTemplateState.selector.get()?.timeStart?.split(" ")[0]
        )?.date
    }

    function getInitialTime(): string | undefined {
        return branding.configuration.notificationTemplatesPage.sctNotificationTemplates.times.find(
            (item: NotificationTemplateTime) =>
                item.start === notificationTemplateState.selector.get()?.timeStart?.split(" ")[0]?.split(":00")[0]
        )?.start
    }

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(notificationTemplate.id.get())
    }, [onClickRemove, notificationTemplate])

    useEffect(() => {
        const timeObject = branding.configuration.notificationTemplatesPage.sctNotificationTemplates.times.find(
            (item: NotificationTemplateTime) => item.id === time
        )

        if (date && timeObject) {
            let selector = JSON.parse(JSON.stringify(notificationTemplate.selector.get()))

            selector!["timeStart"] = `${date} ${timeObject.start}:00`
            selector!["timeEnd"] = `${date} ${timeObject.end}:00`

            notificationTemplate.selector.set(selector)
        }

        //eslint-disable-next-line
    }, [date, time])

    useEffect(() => {
        if (standSpace) {
            let selector = JSON.parse(JSON.stringify(notificationTemplate.selector.get()))

            selector!["geofences"] = [...(selector.geofences || []), { id: standSpace }]

            notificationTemplate.selector.set(selector)
        }

        // eslint-disable-next-line
    }, [standSpace])

    return (
        <Section
            ref={sectionRef}
            title={
                notificationTemplate.text.get()
                    ? truncateWithEllipsis(notificationTemplate.text.get(), 200)
                    : branding.configuration.notificationTemplatesPage.sctNotificationTemplates.titleEmptyNotificationTemplate
            }
            collapsible
            onClickRemove={onClickRemoveHandler}
            entityState={notificationTemplate}
        >
            <Row>
                <Col xs={12}>
                    <Row>
                        <Col>
                            <SelectField
                                name="date"
                                label={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblDate}
                                placeholder={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.hntDate}
                                options={dates}
                                value={date}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
                                required
                            />
                        </Col>
                        <Col>
                            <SelectField
                                name="time"
                                label={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblTime}
                                placeholder={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.hntTime}
                                options={times}
                                value={time}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTime(e.target.value)}
                                required
                            />
                        </Col>
                        <Col>
                            <SelectField
                                name="standSpace"
                                label={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblStandSpace}
                                placeholder={
                                    branding.configuration.notificationTemplatesPage.sctNotificationTemplates.hntStandSpace
                                }
                                options={halls}
                                value={standSpace}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStandSpace(e.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name="text"
                                label={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblText}
                                placeholder={branding.configuration.notificationTemplatesPage.sctNotificationTemplates.hntText}
                                value={getElementFieldValue(notificationTemplate, "text")}
                                onChange={makeSetHandler(notificationTemplate, "text")}
                                limit={400}
                                style={{ height: "100px" }}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <BSForm.Group>
                                <BSForm.Label style={{ marginTop: "20px", marginBottom: "-20px" }}>
                                    {branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblCriteriaList}
                                </BSForm.Label>
                            </BSForm.Group>

                            <PageContents
                                description={
                                    branding.configuration.notificationTemplatesPage.sctNotificationTemplates.hntCriteriaList
                                }
                            >
                                <NotificationTemplateInterests
                                    interests={Array.from(notificationTemplateState.selector.get()?.interests || [])}
                                    refCategories={refCategories}
                                    addInterest={(id: string, credits?: string) => {
                                        let selector = JSON.parse(JSON.stringify(notificationTemplate.selector.get()))

                                        const interests = [
                                            ...(selector?.interests || []),
                                            { id: id, credits: parseFloat(credits || "0") }
                                        ]

                                        selector!["interests"] = interests

                                        notificationTemplate.selector.set(selector)
                                    }}
                                    removeInterest={(id: string, credits?: string) => {
                                        let selector = JSON.parse(JSON.stringify(notificationTemplate.selector.get()))

                                        const interests = (selector?.interests || []).filter(
                                            (interest: NotificationTemplateInterest) => interest.id !== id
                                        )

                                        selector!["interests"] = interests

                                        notificationTemplate.selector.set(selector)
                                    }}
                                />
                            </PageContents>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Section>
    )
}

export default React.memo(withReferenceDataState(NotificationTemplatesSection))
